import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
  Download,
  UploadFile
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import { formatPartMasterOptionLabel } from '../../../utils/partMasterLabel';
import { normalizePartNumber } from '../../../utils/partNumber';
import {
  downloadCsvFile,
  findCsvColumnIndex,
  parseCsvLine,
  parseOptionalCsvDate
} from '../../../utils/csvHelpers';
import { useToast } from '../../../contexts/ToastContext';

const CONSUMABLES_CSV_HEADERS = [
  'Part Number',
  'Reference Part Number',
  'Quantity',
  'Description',
  'Task Reference',
  'Expiry Date',
  'Remarks'
];

/**
 * Component for managing Required Consumables in work orders
 */
const RequiredConsumablesManager = ({ workOrderId, items, onUpdate, workOrderLabel }) => {
  const [localItems, setLocalItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventorySearchInput, setInventorySearchInput] = useState('');
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({
    itemName: '',
    partNumberOrSpecification: '',
    referencePartNumber: '',
    quantity: 1,
    taskReference: '',
    remarks: '',
    expiry: null,
    inventoryItemId: null
  });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const csvUploadInputRef = useRef(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (items) {
      setLocalItems(items);
    } else {
      setLocalItems([]);
    }
  }, [items]);

  // Fetch inventory items with search (optimized with request cancellation)
  const fetchInventoryItems = useCallback(async (searchTerm = '') => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setInventoryLoading(true);
    try {
      const params = {
        category: 'CONSUMABLE',
        includeArchived: false,
        pageSize: 50,
        page: 1
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await axios.get('/api/store/inventory', { 
        params,
        signal: abortController.signal
      });
      
      // Only update if request wasn't aborted
      if (!abortController.signal.aborted) {
        const fetchedItems = response.data.items || response.data || [];
        setInventoryItems(fetchedItems);
      }
    } catch (error) {
      // Ignore abort errors (expected when canceling previous requests)
      if (error.name !== 'CanceledError' && error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error('Error fetching inventory items:', error);
        setInventoryItems([]);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (!abortController.signal.aborted) {
        setInventoryLoading(false);
      }
    }
  }, []);

  // Initialize inventory items on mount
  useEffect(() => {
    fetchInventoryItems('');
  }, [fetchInventoryItems]);

  // Debounced search handler with minimum character threshold
  const handleInventoryInputChange = useCallback((event, newInputValue, reason) => {
    setInventorySearchInput(newInputValue);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If user is typing (not selecting), debounce the search
    if (reason === 'input') {
      const trimmedValue = newInputValue.trim();
      
      // Only search if:
      // 1. User has typed at least 2 characters, OR
      // 2. Input is empty (show initial results)
      if (trimmedValue.length === 0 || trimmedValue.length >= 2) {
        searchTimeoutRef.current = setTimeout(() => {
          fetchInventoryItems(trimmedValue);
        }, 500); // 500ms debounce for better efficiency
      } else if (trimmedValue.length === 1) {
        // For single character, show initial list (no search)
        fetchInventoryItems('');
      }
    } else if (reason === 'clear') {
      // Reset to initial inventory list when cleared
      fetchInventoryItems('');
    }
  }, [fetchInventoryItems]);

  // Ensure selected inventory item is in options
  useEffect(() => {
    if (editFormData.inventoryItemId && !inventoryLoading) {
      const isItemInOptions = inventoryItems.some(item => {
        const itemId = item._id?.toString() || item._id;
        return itemId === editFormData.inventoryItemId;
      });

      // If selected item is not in options, fetch it
      if (!isItemInOptions) {
        axios.get(`/api/store/inventory/${editFormData.inventoryItemId}`)
          .then(response => {
            if (response.data) {
              setInventoryItems(prev => {
                // Check if it's already there to avoid duplicates
                const exists = prev.some(item => {
                  const itemId = item._id?.toString() || item._id;
                  return itemId === editFormData.inventoryItemId;
                });
                return exists ? prev : [...prev, response.data];
              });
            }
          })
          .catch(error => {
            console.error('Error fetching selected inventory item:', error);
          });
      }
    }
  }, [editFormData.inventoryItemId, inventoryItems, inventoryLoading]);

  // Cleanup timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleAdd = () => {
    setEditingIndex(null);
    setEditFormData({
      itemName: '',
      partNumberOrSpecification: '',
      referencePartNumber: '',
      quantity: 1,
      taskReference: '',
      remarks: '',
      expiry: null,
      inventoryItemId: null
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const item = localItems[index];
    setEditFormData({
      itemName: item.itemName || '',
      partNumberOrSpecification: item.partNumberOrSpecification || '',
      referencePartNumber: item.referencePartNumber || '',
      quantity: item.quantity || 1,
      taskReference: item.taskReference || '',
      remarks: item.remarks || '',
      expiry: item.expiry ? new Date(item.expiry) : null,
      inventoryItemId: item.inventoryItemId || null
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.itemName.trim() || !editFormData.partNumberOrSpecification.trim() || !editFormData.quantity || editFormData.quantity < 1) {
      showToast('Item Name, Part Number/Specification, and Quantity (>= 1) are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...editFormData,
        expiry: editFormData.expiry ? editFormData.expiry.toISOString() : null
      };

      if (editingIndex !== null) {
        const item = localItems[editingIndex];
        await axios.put(`/api/maintenance/${workOrderId}/required-consumables/${item._id}`, payload);
      } else {
        await axios.post(`/api/maintenance/${workOrderId}/required-consumables`, payload);
      }
      
      onUpdate();
      setEditDialogOpen(false);
      setEditingIndex(null);
      showToast(editingIndex !== null ? 'Item updated successfully' : 'Item added successfully', 'success');
    } catch (error) {
      console.error('Error saving item:', error);
      showToast(error.response?.data?.message || 'Failed to save item', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index) => {
    const item = localItems[index];
    try {
      await axios.delete(`/api/maintenance/${workOrderId}/required-consumables/${item._id}`);
      onUpdate();
      showToast('Item deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting item:', error);
      showToast(error.response?.data?.message || 'Failed to delete item', 'error');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    try {
      const updated = [...localItems];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      const reordered = updated.map((item, i) => ({
        ...item,
        sequenceNumber: i + 1
      }));
      await axios.put(`/api/maintenance/${workOrderId}/required-consumables/reorder`, {
        items: reordered
      });
      onUpdate();
    } catch (error) {
      console.error('Error reordering items:', error);
      showToast('Failed to reorder items', 'error');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === localItems.length - 1) return;
    try {
      const updated = [...localItems];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      const reordered = updated.map((item, i) => ({
        ...item,
        sequenceNumber: i + 1
      }));
      await axios.put(`/api/maintenance/${workOrderId}/required-consumables/reorder`, {
        items: reordered
      });
      onUpdate();
    } catch (error) {
      console.error('Error reordering items:', error);
      showToast('Failed to reorder items', 'error');
    }
  };

  const handleInventoryItemSelect = (event, value) => {
    if (value) {
      setEditFormData({
        ...editFormData,
        inventoryItemId: value._id,
        partNumberOrSpecification: value.partNumber,
        referencePartNumber: value.referencePartNumber || '',
        itemName: value.description || value.partNumber,
        expiry: value.expiryDate ? new Date(value.expiryDate) : null
      });
    } else {
      setEditFormData({
        ...editFormData,
        inventoryItemId: null
      });
    }
  };

  const handleDownloadCsv = () => {
    const rows = localItems.map((item) => ({
      'Part Number': item.partNumberOrSpecification || '',
      'Reference Part Number': item.referencePartNumber || '',
      Quantity: item.quantity || 1,
      Description: item.itemName || '',
      'Task Reference': item.taskReference || '',
      'Expiry Date': item.expiry ? new Date(item.expiry).toISOString().slice(0, 10) : '',
      Remarks: item.remarks || ''
    }));
    downloadCsvFile(
      `required-consumables-${workOrderLabel || workOrderId || 'work-order'}.csv`,
      CONSUMABLES_CSV_HEADERS,
      rows
    );
  };

  const handleUploadCsvClick = () => {
    csvUploadInputRef.current?.click();
  };

  const handleUploadCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        showToast('CSV has no data rows to import', 'error');
        return;
      }
      const header = parseCsvLine(lines[0]);
      const pnIdx = findCsvColumnIndex(header, [
        'part number',
        'partnumber',
        'pn',
        'part number/specification',
        'part number or specification'
      ]);
      const refPnIdx = findCsvColumnIndex(header, ['reference part number', 'referencepartnumber', 'ref part number', 'ref pn']);
      const qtyIdx = findCsvColumnIndex(header, ['quantity', 'qty']);
      const descIdx = findCsvColumnIndex(header, ['description', 'desc', 'item name', 'itemname']);
      const taskIdx = findCsvColumnIndex(header, ['task reference', 'taskreference']);
      const expiryIdx = findCsvColumnIndex(header, ['expiry date', 'expirydate', 'expiry']);
      const remarksIdx = findCsvColumnIndex(header, ['remarks', 'remark']);
      if (pnIdx < 0 || qtyIdx < 0) {
        showToast('CSV must include "Part Number" and "Quantity" columns', 'error');
        return;
      }

      const importedRows = lines.slice(1).map((line) => parseCsvLine(line)).map((columns) => {
        const partNumber = normalizePartNumber(columns[pnIdx]);
        if (!partNumber) return null;
        const qtyRaw = Number(columns[qtyIdx]);
        const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
        const description = descIdx >= 0 ? String(columns[descIdx] || '').trim() : '';
        const expiry = expiryIdx >= 0 ? parseOptionalCsvDate(columns[expiryIdx]) : null;
        const referencePartNumber = refPnIdx >= 0 ? normalizePartNumber(columns[refPnIdx]) : '';
        return {
          partNumberOrSpecification: partNumber,
          ...(referencePartNumber ? { referencePartNumber } : {}),
          quantity,
          itemName: description || partNumber,
          taskReference: taskIdx >= 0 ? String(columns[taskIdx] || '').trim() : '',
          expiry: expiry ? expiry.toISOString() : null,
          remarks: remarksIdx >= 0 ? String(columns[remarksIdx] || '').trim() : ''
        };
      }).filter(Boolean);

      if (!importedRows.length) {
        showToast('No valid consumable rows found in CSV', 'error');
        return;
      }

      setImporting(true);
      for (const row of importedRows) {
        await axios.post(`/api/maintenance/${workOrderId}/required-consumables`, row);
      }
      onUpdate();
      showToast(`Imported ${importedRows.length} consumable row(s)`, 'success');
    } catch (error) {
      console.error('Error importing consumables CSV:', error);
      showToast(error.response?.data?.message || 'Failed to import CSV', 'error');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(expiryDate) <= thirtyDaysFromNow && new Date(expiryDate) >= new Date();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Required Consumables</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={handleDownloadCsv}
            disabled={localItems.length === 0 || importing}
          >
            Download CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFile />}
            onClick={handleUploadCsvClick}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Upload CSV'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={handleAdd}
            disabled={importing}
          >
            Add Consumable
          </Button>
        </Box>
      </Box>
      <input
        type="file"
        ref={csvUploadInputRef}
        onChange={handleUploadCsv}
        accept=".csv,text/csv"
        style={{ display: 'none' }}
      />

      {localItems.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No consumables added. Click "Add Consumable" to add one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="80">S/N</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Part Number/Specification</TableCell>
                <TableCell>Reference Part Number</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Task Reference</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell width="150">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localItems.map((item, index) => {
                const expired = isExpired(item.expiry);
                const expiringSoon = isExpiringSoon(item.expiry);
                return (
                  <TableRow key={item._id || index}>
                    <TableCell>{item.sequenceNumber}</TableCell>
                    <TableCell>{item.itemName}</TableCell>
                    <TableCell>{item.partNumberOrSpecification}</TableCell>
                    <TableCell>{item.referencePartNumber || '—'}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.taskReference || '-'}</TableCell>
                    <TableCell>
                      {item.expiry ? (
                        <Box>
                          <Typography variant="body2">
                            {new Date(item.expiry).toLocaleDateString()}
                          </Typography>
                          {expired && (
                            <Chip label="Expired" color="error" size="small" sx={{ mt: 0.5 }} />
                          )}
                          {expiringSoon && !expired && (
                            <Chip label="Expiring Soon" color="warning" size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{item.remarks || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === localItems.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(index)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit Consumable' : 'Add Consumable'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={inventoryItems}
              getOptionLabel={(option) => formatPartMasterOptionLabel(option)}
              value={inventoryItems.find(item => item._id === editFormData.inventoryItemId) || null}
              onChange={handleInventoryItemSelect}
              loading={inventoryLoading}
              onInputChange={handleInventoryInputChange}
              inputValue={inventorySearchInput}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Inventory Item (Optional)"
                  placeholder="Search by part number or reference part number..."
                />
              )}
            />
            <TextField
              fullWidth
              label="Item Name *"
              value={editFormData.itemName}
              onChange={(e) => setEditFormData({ ...editFormData, itemName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Part Number or Specification *"
              value={editFormData.partNumberOrSpecification}
              onChange={(e) => setEditFormData({ ...editFormData, partNumberOrSpecification: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Reference Part Number"
              value={editFormData.referencePartNumber}
              onChange={(e) => setEditFormData({ ...editFormData, referencePartNumber: e.target.value })}
              placeholder="Optional — autofills from inventory/parts catalog"
            />
            <TextField
              fullWidth
              label="Quantity *"
              type="number"
              value={editFormData.quantity}
              onChange={(e) => setEditFormData({ ...editFormData, quantity: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1 }}
              required
            />
            <TextField
              fullWidth
              label="Task Reference"
              value={editFormData.taskReference}
              onChange={(e) => setEditFormData({ ...editFormData, taskReference: e.target.value })}
              placeholder="Optional"
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Expiry Date (Optional)"
                value={editFormData.expiry}
                onChange={(date) => setEditFormData({ ...editFormData, expiry: date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Remarks"
              value={editFormData.remarks}
              onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
              placeholder="Optional"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading || !editFormData.itemName.trim() || !editFormData.partNumberOrSpecification.trim() || !editFormData.quantity || editFormData.quantity < 1}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequiredConsumablesManager;
